/**
 * AuthContext — JWT en mémoire + refresh en sessionStorage.
 * Pattern identique à frontend/src/context/AuthContext.jsx (poc_gemini).
 * Migré en TypeScript strict pour apps/admin.
 *
 * IMPORTANT (Sprint chat-PWA) : le token est propagé au TokenManager du SDK
 * afin que TOUS les appels passant par le SDK (upload, SSE agentStream, RAG,
 * HITL) soient authentifiés (Bearer). Sans ce câblage, le SDK partait sans
 * token et le backend renvoyait 401 (bascule silencieuse en simulation).
 * AuthProvider DOIT donc rester monté à l'intérieur de SDKProvider.
 *
 * AUTH-02 — Refresh silencieux au démarrage :
 * Si un refresh_token est présent dans sessionStorage, AuthProvider appelle
 * POST /auth/refresh au mount avant de rendre les enfants. Pendant ce temps,
 * `bootstrapping = true` et RequireAuth affiche un spinner plutôt que de
 * rediriger vers /login — évitant la déconnexion au F5/reload.
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { DataClearance } from '@assistia/sdk';
import { useSDK } from './SDKContext.tsx';
import { clearChatStorage } from '../utils/threadStorage.ts';

/**
 * Rôles applicatifs reconnus pour le RBAC frontend.
 * Aligné sur l'enum backend `UserRole` : root (super-admin) > admin > manager > user.
 * Le SDK ne type que les rôles *assignables* via l'UI (admin/manager/user) ;
 * `root` est reconnu ici pour le gating mais non assignable depuis l'admin.
 */
export type AppRole = 'root' | 'admin' | 'manager' | 'user' | 'auditor';

/** Rôles ayant les privilèges d'administration (gating des pages sensibles). */
export const ADMIN_ROLES: readonly AppRole[] = ['root', 'admin'];

interface AuthContextValue {
  isAuthenticated: boolean;
  accessToken: string | null;
  /** Rôle de l'utilisateur courant (récupéré via /api/v1/auth/me). */
  role: AppRole | null;
  /** Habilitation données de l'utilisateur courant (C1–C4), via /auth/me. */
  clearance: DataClearance | null;
  /** True tant que le profil (rôle + clearance) est en cours de récupération. */
  roleLoading: boolean;
  /**
   * True pendant le refresh silencieux au démarrage (AUTH-02).
   * RequireAuth affiche un spinner tant que cette valeur est true,
   * pour ne pas rediriger vers /login avant que le refresh soit terminé.
   */
  bootstrapping: boolean;
  login: (accessToken: string, refreshToken: string) => void;
  logout: () => void;
  getToken: () => string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function isAppRole(v: unknown): v is AppRole {
  return v === 'root' || v === 'admin' || v === 'manager' || v === 'user' || v === 'auditor';
}

function isClearance(v: unknown): v is DataClearance {
  return v === 'C1' || v === 'C2' || v === 'C3' || v === 'C4';
}

export function AuthProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const { sdk } = useSDK();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [clearance, setClearance] = useState<DataClearance | null>(null);
  const [roleLoading, setRoleLoading] = useState(false);
  // AUTH-02 : true tant que le refresh silencieux au démarrage est en cours.
  // Initialisé à true seulement si un refresh_token existe en sessionStorage
  // (sinon pas d'attente inutile — l'utilisateur n'est pas connecté).
  const [bootstrapping, setBootstrapping] = useState<boolean>(
    () => !!sessionStorage.getItem('assistia_refresh'),
  );
  // Ref pour éviter le double-appel en StrictMode (double mount React 18).
  const bootstrapRan = useRef(false);

  const login = useCallback((access: string, refresh: string) => {
    setAccessToken(access);
    sessionStorage.setItem('assistia_refresh', refresh);
    // Propage le couple de tokens au SDK (Bearer pour REST + SSE).
    sdk.tokenManager.setTokens(access, refresh);
  }, [sdk]);

  const logout = useCallback(() => {
    setAccessToken(null);
    setRole(null);
    setClearance(null);
    sessionStorage.removeItem('assistia_refresh');
    // Réinitialise les tokens côté SDK (valeurs vides = aucun Bearer émis).
    sdk.tokenManager.setTokens('', '');
    // 7.3-05 : purge du fil conversationnel persisté localement.
    void clearChatStorage();
  }, [sdk]);

  const getToken = useCallback(() => accessToken, [accessToken]);

  // AUTH-02 — Refresh silencieux au démarrage.
  // Une seule tentative par mount : si sessionStorage contient un refresh_token,
  // on appelle POST /auth/refresh avant que RequireAuth ne soit évalué.
  useEffect(() => {
    const stored = sessionStorage.getItem('assistia_refresh');
    if (!stored || bootstrapRan.current) return;
    bootstrapRan.current = true;

    const base = import.meta.env.VITE_API_BASE_URL ?? '';
    fetch(`${base}/api/v1/auth/refresh?refresh_token=${encodeURIComponent(stored)}`, {
      method: 'POST',
    })
      .then((r) => (r.ok ? (r.json() as Promise<{ access_token: string; refresh_token: string }>) : null))
      .then((data) => {
        if (data?.access_token && data?.refresh_token) {
          // login() propage les tokens au SDK et déclenche le fetch /auth/me
          setAccessToken(data.access_token);
          sessionStorage.setItem('assistia_refresh', data.refresh_token);
          sdk.tokenManager.setTokens(data.access_token, data.refresh_token);
        } else {
          // Refresh invalide ou expiré → déconnexion propre
          sessionStorage.removeItem('assistia_refresh');
        }
      })
      .catch(() => {
        sessionStorage.removeItem('assistia_refresh');
      })
      .finally(() => {
        setBootstrapping(false);
      });
  }, []); // Mount uniquement — sdk stable, pas de dépendances changeantes

  // Récupère le rôle courant via /auth/me dès qu'un token est disponible.
  useEffect(() => {
    if (!accessToken) {
      setRole(null);
      setClearance(null);
      return;
    }
    let cancelled = false;
    setRoleLoading(true);
    const base = import.meta.env.VITE_API_BASE_URL ?? '';
    fetch(`${base}/api/v1/auth/me`, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { role?: unknown; clearance?: unknown } | null) => {
        if (cancelled) return;
        setRole(data && isAppRole(data.role) ? data.role : null);
        setClearance(data && isClearance(data.clearance) ? data.clearance : null);
      })
      .catch(() => {
        if (!cancelled) {
          setRole(null);
          setClearance(null);
        }
      })
      .finally(() => {
        if (!cancelled) setRoleLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  return (
    <AuthContext.Provider value={{
      isAuthenticated: !!accessToken,
      accessToken,
      role,
      clearance,
      roleLoading,
      bootstrapping,
      login,
      logout,
      getToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
